/**
 * Calculadora interativa de precos para fornecedores.
 *
 * Modelo FLAT: ao atingir uma faixa, TODOS os moradores sao cobrados pelo
 * valor daquela faixa. Isso incentiva o fornecedor a crescer, porque ao
 * cruzar o limite de uma faixa o custo total pode CAIR.
 */
import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import GroupsIcon from '@mui/icons-material/Groups';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

/**
 * Faixas de preco (espelho do backend PRICING.tiers).
 * Taxa FLAT: ao entrar na faixa, todos os moradores ficam nesse valor.
 */
const TIERS = [
  { upTo: 500, pricePerResident: 0.05, label: 'Até 500' },
  { upTo: 1000, pricePerResident: 0.04, label: '501 – 1.000' },
  { upTo: 5000, pricePerResident: 0.03, label: '1.001 – 5.000' },
  { upTo: 10000, pricePerResident: 0.02, label: '5.001 – 10.000' },
  { upTo: 20000, pricePerResident: 0.01, label: '10.001 – 20.000' },
  { upTo: Infinity, pricePerResident: 0.01, label: 'Acima de 20.000' },
];

const BASE_PRICE = 50;

const PLAN_NAMES = {
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
  enterprise: 'Enterprise',
};

const PLAN_COLORS = {
  starter: 'default',
  growth: 'info',
  scale: 'warning',
  enterprise: 'secondary',
};

/**
 * Escala nao-linear do slider.
 *
 * O MUI Slider e linear; para que a faixa 0–1.000 (passos de 100) ocupe o mesmo
 * espaco visual que a faixa 1.000–25.000 (passos de 1.000), o slider opera sobre
 * o INDICE de uma lista de valores ancorados uniformemente. Cada indice ocupa a
 * mesma largura na barra, entao os marcos ficam bem espacados.
 */
const SLIDER_VALUES = [
  // 0 a 1.000 de 100 em 100
  0, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000,
  // 1.000 a 25.000 de 1.000 em 1.000
  2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000, 11000, 12000, 13000,
  14000, 15000, 16000, 17000, 18000, 19000, 20000, 21000, 22000, 23000, 24000, 25000,
];

const SLIDER_MAX = SLIDER_VALUES.length - 1;
const SLIDER_CAP = SLIDER_VALUES[SLIDER_MAX];

/** Indice da escala -> quantidade de moradores. */
const scaleToResidents = (index) => SLIDER_VALUES[Math.max(0, Math.min(SLIDER_MAX, index))];

/** Quantidade de moradores -> indice da escala (o mais proximo que nao ultrapassa). */
function residentsToScale(residents) {
  const value = Math.min(residents, SLIDER_CAP);
  let idx = 0;
  for (let i = 0; i < SLIDER_VALUES.length; i += 1) {
    if (SLIDER_VALUES[i] <= value) idx = i;
    else break;
  }
  return idx;
}

/** Marcos posicionados pelo indice (espacamento visual uniforme). */
const SLIDER_MARKS = [
  { real: 500, label: '500' },
  { real: 1000, label: '1k' },
  { real: 5000, label: '5k' },
  { real: 10000, label: '10k' },
  { real: 20000, label: '20k' },
  { real: 25000, label: '25k' },
].map((m) => ({ value: residentsToScale(m.real), label: m.label }));

function formatBRL(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatBRLShort(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Calcula com taxa FLAT: encontra a faixa e aplica a todos os moradores.
 */
function calculate(residents) {
  const count = Math.max(0, Math.floor(residents));

  const tierIndex = TIERS.findIndex((t) => count <= t.upTo);
  const tier = tierIndex >= 0 ? TIERS[tierIndex] : TIERS[TIERS.length - 1];
  const rate = tier.pricePerResident;
  const variable = Math.round(count * rate * 100) / 100;
  const total = BASE_PRICE + variable;

  let suggestedPlan = 'starter';
  if (count > 20000) suggestedPlan = 'enterprise';
  else if (count > 5000) suggestedPlan = 'scale';
  else if (count > 500) suggestedPlan = 'growth';

  // Calcula quanto custaria na faixa anterior (para mostrar a economia)
  let savingsVsPrevious = null;
  if (tierIndex > 0) {
    const prevTier = TIERS[tierIndex - 1];
    const prevCost = BASE_PRICE + count * prevTier.pricePerResident;
    savingsVsPrevious = Math.round((prevCost - total) * 100) / 100;
  }

  return { total, base: BASE_PRICE, variable, rate, tier, tierIndex, suggestedPlan, count, savingsVsPrevious };
}

export default function PricingCalculator() {
  const [residents, setResidents] = useState(1000);

  const result = useMemo(() => calculate(residents), [residents]);

  function handleSliderChange(_e, index) {
    setResidents(scaleToResidents(index));
  }

  function handleInputChange(e) {
    const value = e.target.value === '' ? 0 : Number(e.target.value);
    if (Number.isFinite(value) && value >= 0 && value <= 100000) {
      setResidents(value);
    }
  }

  return (
    <Card
      elevation={0}
      sx={{
        border: '2px solid',
        borderColor: 'secondary.main',
        borderRadius: 2,
        overflow: 'visible',
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 5 } }}>
        {/* Header */}
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 3 }}>
          <AttachMoneyIcon color="secondary" fontSize="large" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              Calculadora de investimento
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ajuste a quantidade de moradores e veja seu custo. Ao subir de faixa, o valor por morador cai para todos.
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={4}>
          {/* Lado esquerdo: controles */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={3}>
              {/* Input + Slider */}
              <Box>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 1 }}>
                  <GroupsIcon color="action" />
                  <Typography variant="subtitle2">
                    Moradores alcançados
                  </Typography>
                  <TextField
                    value={residents}
                    onChange={handleInputChange}
                    type="number"
                    size="small"
                    inputProps={{ min: 0, max: 100000, step: 100, 'aria-label': 'Quantidade de moradores' }}
                    sx={{ width: 120 }}
                  />
                </Stack>
                <Slider
                  value={residentsToScale(residents)}
                  onChange={handleSliderChange}
                  min={0}
                  max={SLIDER_MAX}
                  step={1}
                  marks={SLIDER_MARKS}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(index) => scaleToResidents(index).toLocaleString('pt-BR')}
                  color="secondary"
                  aria-label="Quantidade de moradores"
                  sx={{ mt: 1 }}
                />
              </Box>

              {/* Calculo detalhado */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Como é calculado
                </Typography>

                <Stack spacing={1}>
                  <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Typography variant="body2" color="text.secondary">
                      Ativação mensal (fixa)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {formatBRL(BASE_PRICE)}
                    </Typography>
                  </Stack>

                  <Divider />

                  <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Typography variant="body2" color="text.secondary">
                      {result.count.toLocaleString('pt-BR')} moradores × {formatBRL(result.rate)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {formatBRL(result.variable)}
                    </Typography>
                  </Stack>

                  <Divider />

                  <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Total mensal
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main', whiteSpace: 'nowrap' }}>
                      {formatBRL(result.total)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Alerta de economia ao subir de faixa */}
              {result.savingsVsPrevious > 0 && (
                <Alert severity="success" icon={<TrendingDownIcon />}>
                  Você está economizando <strong>{formatBRL(result.savingsVsPrevious)}/mês</strong> por
                  estar nessa faixa! Todos os {result.count.toLocaleString('pt-BR')} moradores
                  custam apenas {formatBRL(result.rate)} cada.
                </Alert>
              )}

              {/* Tabela de faixas com destaque na atual */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Faixas disponíveis (sua faixa atual está destacada)
                </Typography>
                <Stack spacing={0.5}>
                  {TIERS.map((tier, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      spacing={1.5}
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: i === result.tierIndex ? 'secondary.50' : 'transparent',
                        border: i === result.tierIndex ? '1px solid' : '1px solid transparent',
                        borderColor: i === result.tierIndex ? 'secondary.main' : 'transparent',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: i === result.tierIndex ? 700 : 400 }}>
                        {tier.label} moradores
                      </Typography>
                      <Chip
                        label={`${formatBRL(tier.pricePerResident)}/morador`}
                        size="small"
                        color={i === result.tierIndex ? 'secondary' : 'default'}
                        variant={i === result.tierIndex ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.7rem', flexShrink: 0 }}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Grid>

          {/* Lado direito: resultado */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                bgcolor: 'background.default',
                borderRadius: 3,
                p: 3,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.15em', display: 'block', mb: 0.5 }}>
                Seu investimento mensal
              </Typography>
              <Typography
                variant="h2"
                color="secondary.main"
                sx={{ fontWeight: 800, my: 1.5, fontSize: { xs: 36, md: 48 }, lineHeight: 1.1 }}
              >
                {formatBRLShort(result.total)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                por mês
              </Typography>

              <Divider sx={{ my: 2.5 }} />

              {/* Taxa por morador */}
              <Typography variant="body1" sx={{ mb: 0.75 }}>
                <strong>{formatBRL(result.rate)}</strong> por morador
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                Taxa única aplicada a todos os {result.count.toLocaleString('pt-BR')} moradores
              </Typography>

              <Divider sx={{ my: 2.5 }} />

              {/* Plano sugerido */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Plano recomendado
                </Typography>
                <Chip
                  label={PLAN_NAMES[result.suggestedPlan]}
                  color={PLAN_COLORS[result.suggestedPlan]}
                  sx={{ fontWeight: 700, fontSize: '0.85rem' }}
                />
              </Box>

              {/* Dica: proxima faixa */}
              {result.tierIndex > 0 && result.tierIndex < TIERS.length - 1 && (
                <Box sx={{ mt: 2.5, p: 1.5, bgcolor: 'success.50', borderRadius: 2 }}>
                  <Typography variant="caption" color="success.dark">
                    Próxima economia: ao atingir{' '}
                    <strong>{(TIERS[result.tierIndex].upTo + 1).toLocaleString('pt-BR')}</strong> moradores,
                    todos passam a custar{' '}
                    <strong>{formatBRL(TIERS[result.tierIndex + 1].pricePerResident)}</strong>
                  </Typography>
                </Box>
              )}
              {result.tierIndex === 0 && result.count > 0 && (
                <Box sx={{ mt: 2.5, p: 1.5, bgcolor: 'info.50', borderRadius: 2 }}>
                  <Typography variant="caption" color="info.dark">
                    Dica: ao atingir <strong>501</strong> moradores, todos passam a custar{' '}
                    <strong>{formatBRL(TIERS[1].pricePerResident)}</strong> — o total cai!
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

      </CardContent>
    </Card>
  );
}
